import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { expensesAPI } from '../../services/api';
import { Expense } from '../../types/expense';

interface RejectExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense: Expense;
  onSuccess: () => void;
}

const RejectExpenseModal: React.FC<RejectExpenseModalProps> = ({
  isOpen,
  onClose,
  expense,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();

  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!reason.trim()) {
      setError(t('expense.rejectionReasonRequired') || 'Rejection reason is required');
      return;
    }

    setSubmitting(true);
    try {
      await expensesAPI.reject(expense.id, reason);
      showToast(t('expense.rejectSuccess') || 'Expense rejected successfully', 'success');
      setReason('');
      onSuccess();
      onClose();
    } catch (err) {
      showToast(t('expense.rejectFailed') || 'Failed to reject expense', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const cardClass = isDark
    ? 'bg-slate-800 border border-slate-700'
    : 'bg-white border border-slate-200';

  const labelClass = isDark ? 'text-slate-300' : 'text-slate-700';
  const inputClass = isDark
    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500';

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full max-w-md transform rounded-2xl p-6 shadow-xl transition-all ${cardClass}`}>
          {/* Close Button */}
          <button
            type="button"
            onClick={handleClose}
            className={`absolute top-4 right-4 p-1 rounded-lg transition-colors ${
              isDark
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          {/* Title */}
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {t('expense.rejectTitle') || 'Reject Expense'}
          </h3>

          {/* Expense Summary */}
          <div
            className={`mt-4 p-3 rounded-lg ${
              isDark
                ? 'bg-slate-700/50 border border-slate-600'
                : 'bg-slate-50 border border-slate-200'
            }`}
          >
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className={labelClass}>{t('expense.employee') || 'Employee'}:</span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>
                  {expense.user.full_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={labelClass}>{t('expense.amount') || 'Amount'}:</span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>
                  {expense.currency} {parseFloat(expense.amount).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={labelClass}>{t('expense.category') || 'Category'}:</span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>
                  {expense.category}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={labelClass}>{t('expense.date') || 'Date'}:</span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>
                  {new Date(expense.expense_date).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Rejection Reason */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${labelClass}`}>
                {t('expense.rejectionReason') || 'Rejection Reason'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                placeholder={t('expense.rejectionReasonPlaceholder') || 'Please provide a reason for rejecting this expense...'}
                className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
                rows={4}
                disabled={submitting}
              />
              {error && (
                <p className="mt-2 text-sm text-red-500">{error}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                  isDark
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                } disabled:opacity-50`}
              >
                {t('expense.cancel') || 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={submitting || !reason.trim()}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {submitting
                  ? (t('expense.rejecting') || 'Rejecting...')
                  : (t('expense.reject') || 'Reject')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RejectExpenseModal;
